// --------------------------------------------------------------
// Created On: 2026-11-10
// Author: Zachary Thomas
//
// Last Modified: 2026-11-03
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import {
  LOCAL_STORAGE_DISABLE_DASHBOARD_INACTIVE_CHECK_KEY,
  LOCAL_STORAGE_SELECTED_ASSETGROUP_ID_KEY,
  LOCAL_STORAGE_SELECTED_DASHBOARD_KEY,
} from "src/constants/localStorageVariables";
import React, { useState, useEffect, Fragment, useMemo } from "react";
import { Active, DndContext, Over } from "@dnd-kit/core";
import Card from "src/components/Card/Card";
import useReduxSelector from "src/hooks/useReduxSelector";
import { UPDATE_ASSET_GROUPS_PERMISSION } from "src/constants/permissions";
import { API, MS_PER_SECOND } from "src/constants/miscellaneous";
import {
  DASHBOARD_SYSTEM_CONTROL_POLLING_DELAY_SECONDS,
  DASHBOARD_SYSTEM_CONTROL_MAX_INACTIVE_USER_SECONDS,
} from "src/constants/polling";
import useLastActive from "src/hooks/useLastActive";
import GetAssetGroupSystemControlDataResponseBody from "src/types/api/GetAssetGroupSystemControlDataResponseBody";
import apiRequest from "src/utilities/api/apiRequest";
import getApiError from "src/utilities/api/getApiError";
import useApi from "src/hooks/useApi";
import GetAssetGroupSystemControlConfigurationResponseBody from "src/types/api/GetAssetGroupSystemControlConfigurationResponseBody";
import GetSystemControlMapResponseBody from "src/types/api/GetSystemControlMapResponseBody";
import PutAssetGroupSystemControlResponseBody from "src/types/api/PutAssetGroupSystemControlResponseBody";
import SaveChangesModal from "src/components/SaveChangesModal/SaveChangesModal";
import GroupAndEditSelector from "./GroupAndEditSelector/GroupAndEditSelector";
import SystemControlComponentWrapper from "./SystemControlComponentWrapper/SystemControlComponentWrapper";
import AddResourceButton from "src/components/AddResourceButton/AddResourceButton";
import userHasPermission from "src/utilities/userHasPermission";
import Spinner from "src/components/Spinner/Spinner";
import Toast from "src/components/Toast/Toast";
import deepCopy from "src/utilities/deepCopy";
import { GAUGE_COMPONENT_TYPE, TABLE_COMPONENT_TYPE, GAUGE_TYPE_TANK_ROUND } from "src/constants/systemControl";
import styles from "./DashboardSystemControlPage.module.scss";

// System control page for managing a collection of assets with different info and controls.
export default function DashboardSystemControlPage(): Component {
  const [mapLoading, setMapLoading] = useState<boolean>(false);
  const [componentLoading, setComponentLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [assetgroups, setAssetgroups] = useState<DashboardAssetgroup[]>([]);
  const [assetgroupId, setAssetgroupId] = useState<number>(0);
  const [assetGroupLastModifiedUtc, setAssetGroupLastModifiedUtc] = useState<string | null>(null);
  const [previousComponents, setPreviousComponents] = useState<SystemControlComponent[]>([]);
  const [components, setComponents] = useState<SystemControlComponent[]>([]);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [enumerationMap, setEnumerationMap] = useState<EnumerationMap>({});
  const [assets, setAssets] = useState<Asset[]>([]);
  const [showCancelChangesModal, setShowCancelChangesModal] = useState<boolean>(false);
  const [inEditMode, setInEditMode] = useState<boolean>(false);
  const [unableToSaveErrorMessage, setUnableToSaveErrorMessage] = useState<string>("");
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [pollCounter, setPollCounter] = useState<number>(0);
  const [timerCounter, setTimerCounter] = useState<number>(0);
  const [userPreviouslyInactive, setUserPreviouslyInactive] = useState<boolean>(false);
  const userInactiveSeconds = useLastActive(DASHBOARD_SYSTEM_CONTROL_POLLING_DELAY_SECONDS);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const assetMap = useMemo(() => getAssetMap(assets), [JSON.stringify(assets)]);
  const attributeMap = useMemo(() => getAttributeMap(attributes), [JSON.stringify(attributes)]);
  const MAX_COMPONENTS_IN_SYSTEM_CONTROL = 50;

  // Remember that the system control was the last dashboard viewed so "/dashboard" redirects back here.
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_SELECTED_DASHBOARD_KEY, "COMMAND");
  }, []);

  // Get data to help support creating and managing components.
  useApi(
    () => {
      setMapLoading(true);
      setAssetgroups([]);
      return true;
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/command-center`,
    },
    async (response: Response, responseBody: GetSystemControlMapResponseBody) => {
      if (response.ok && responseBody) {
        setFailedToLoad(false);
        setAttributes(responseBody.attributes);
        setEnumerationMap(responseBody.enumerationMap);
        setAssetgroups(responseBody.assetgroups);

        // Attempt to load saved asset group ID.
        let assetgroupId: number | string | null = localStorage.getItem(LOCAL_STORAGE_SELECTED_ASSETGROUP_ID_KEY);
        if (assetgroupId !== null) {
          assetgroupId = parseInt(assetgroupId, 10);
          if (listIncludesAssetGroupId(responseBody.assetgroups, assetgroupId)) {
            setAssetgroupId(assetgroupId);
          }
        }
      } else {
        setFailedToLoad(true);
      }
      setMapLoading(false);
    },
    [currentUser.companyId]
  );

  // Get the structural data for all components.
  useApi(
    () => {
      if (assetgroupId !== 0) {
        setComponentLoading(true);
        return true;
      } else {
        setComponents([]);
        setAssets([]);
        setPreviousComponents([]);
        return false;
      }
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/assetgroup/${assetgroupId}/command-center/config`,
    },
    async (response: Response, responseBody: GetAssetGroupSystemControlConfigurationResponseBody) => {
      if (response.ok && responseBody) {
        setAssetGroupLastModifiedUtc(responseBody.assetgroupModifiedUtc);
        setAssets(responseBody.assetsInAssetgroup);
        // For V1 of the system control feature we only support a single asset for a component. In future versions,
        // we will support multiple assets per component. Since we are going to start by supporting a single asset per
        // component, we have to store the asset name at the component level to allow preserving the asset name when
        // all attributes are deleted.
        const updatedComponents: SystemControlComponent[] = responseBody.components.map((component) => ({
          ...component,
          attributes: component.attributes.map((attribute) => ({ ...attribute, modifiedSinceLoad: false })),
          assetName: component.attributes.at(0)?.assetName ?? "",
          remoteOperationEnabled: component.remoteOperationAsset !== null,
        }));
        setComponents(updatedComponents);
        setPreviousComponents(updatedComponents);
        setFailedToLoad(false);
      } else {
        setFailedToLoad(true);
      }
      setComponentLoading(false);
    },
    [assetgroupId, assetGroupLastModifiedUtc]
  );

  // Poll the latest attribute values for all components. The configuration request supplies the initial values.
  useApi(
    () => {
      if (assetgroupId !== 0) {
        return true;
      } else {
        return false;
      }
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/assetgroup/${assetgroupId}/command-center/data`,
    },
    async (response: Response, responseBody: GetAssetGroupSystemControlDataResponseBody) => {
      if (response.ok && responseBody) {
        // Using the response, we want to update the values in the components to have the new values and timestamps.
        const updatedComponents = (prev: SystemControlComponent[]): SystemControlComponent[] =>
          prev.map((component) => ({
            ...component,
            attributes: component.attributes.map((attribute) => {
              const newValue =
                responseBody.assetAttributesByComponentId[component.commandCenterComponentId]?.[
                  attribute.componentAssetAttributeId
                ];
              // Attributes that were changed while editing are no longer the attribute the data belongs to.
              if (newValue === undefined || attribute.modifiedSinceLoad === true) {
                return attribute;
              } else {
                return { ...attribute, currentValue: newValue };
              }
            }),
          }));
        setComponents(updatedComponents);
        setPreviousComponents(updatedComponents);

        // If the system control was changed elsewhere, reload the configuration unless the user is currently editing.
        if (
          !inEditMode &&
          assetGroupLastModifiedUtc !== null &&
          responseBody.assetgroupModifiedUtc !== assetGroupLastModifiedUtc
        ) {
          setAssetGroupLastModifiedUtc(responseBody.assetgroupModifiedUtc);
        }
      }
    },
    [pollCounter]
  );

  // Periodically poll data to give users a live feed.
  useEffect(() => {
    const newTimerId = setTimeout(() => {
      setTimerCounter((prev) => prev + 1);
      // Only poll data if the user is active.
      const inactiveCheckDisabled = localStorage.getItem(LOCAL_STORAGE_DISABLE_DASHBOARD_INACTIVE_CHECK_KEY) === "true";
      if (inactiveCheckDisabled) {
        console.log("Looking to see if data should be polled. Inactivity is not considered due to user settings.");
      } else {
        console.log(
          `Looking to see if data should be polled` +
            ` (inactive for ${userInactiveSeconds} seconds of ${DASHBOARD_SYSTEM_CONTROL_MAX_INACTIVE_USER_SECONDS} second limit).`
        );
      }
      if (inactiveCheckDisabled || userInactiveSeconds < DASHBOARD_SYSTEM_CONTROL_MAX_INACTIVE_USER_SECONDS) {
        if (assetgroupId !== 0) {
          console.log("Polling component data.");
          setPollCounter((prev) => prev + 1);
          setUserPreviouslyInactive(false);
        } else {
          console.log("Data cannot be polled while an asset group is not selected.");
          setUserPreviouslyInactive(false);
        }
      } else {
        console.log("User is inactive. Data will not be polled.");
        setUserPreviouslyInactive(true);
      }
    }, DASHBOARD_SYSTEM_CONTROL_POLLING_DELAY_SECONDS * MS_PER_SECOND);
    return () => {
      clearTimeout(newTimerId);
    };
  }, [timerCounter, assetgroupId]);

  // If the user returns to the page after being inactive, trigger polling data immediately instead of waiting for timer.
  useEffect(() => {
    if (userPreviouslyInactive && userInactiveSeconds === 0 && assetgroupId !== 0) {
      setPollCounter((prev) => prev + 1);
    }
  }, [userInactiveSeconds, userPreviouslyInactive]);

  // Returns whether the current asset group list includes a specific asset group ID.
  function listIncludesAssetGroupId(assetgroups: DashboardAssetgroup[], assetgroupId: number): boolean {
    return assetgroupId === 0 || assetgroups.some((assetgroup) => assetgroup.assetgroupId === assetgroupId);
  }

  // Handle changing the asset name associated with a component.
  function handleChangeAssetName(systemControlComponentId: number, assetName: string): void {
    const componentsDeepCopy = deepCopy(components);
    const selectedComponent = componentsDeepCopy.find(
      (component) => component.commandCenterComponentId === systemControlComponentId
    );
    if (selectedComponent !== undefined) {
      // Check if the current asset name is a valid asset.
      let newAssetId = 0;
      const mappedAsset = assetMap[assetName];
      if (mappedAsset === undefined) {
        selectedComponent.remoteOperationAsset = null;
      } else {
        newAssetId = mappedAsset.assetId;
        if (selectedComponent.remoteOperationEnabled) {
          selectedComponent.remoteOperationAsset = {
            assetId: mappedAsset.assetId,
            assetName: mappedAsset.assetName,
            deviceType: mappedAsset.deviceModel || "",
            userRemoteOperationAllowed: mappedAsset.remoteOperationAllowed,
            controllerModel: mappedAsset.controllerModel,
            digitalOutputControls: [],
          };
        }
      }
      selectedComponent.attributes.forEach((attribute) => {
        attribute.assetId = newAssetId;
        attribute.assetName = assetName;
        updateLiveDataState(systemControlComponentId, attribute);
      });
      selectedComponent.assetName = assetName;
      setComponents(componentsDeepCopy);
    }
  }

  // Handle changing if a component supports remote operation or not.
  function handleToggleRemoteOperationEnabled(systemControlComponentId: number): void {
    const componentsDeepCopy = deepCopy(components);
    const selectedComponent = componentsDeepCopy.find(
      (component) => component.commandCenterComponentId === systemControlComponentId
    );
    if (selectedComponent !== undefined) {
      if (selectedComponent.remoteOperationEnabled) {
        selectedComponent.remoteOperationEnabled = false;
        selectedComponent.remoteOperationAsset = null;
      } else {
        selectedComponent.remoteOperationEnabled = true;
        const mappedAsset = assetMap[selectedComponent.assetName];
        if (mappedAsset === undefined) {
          selectedComponent.remoteOperationAsset = null;
        } else {
          selectedComponent.remoteOperationAsset = {
            assetId: mappedAsset.assetId,
            assetName: mappedAsset.assetName,
            deviceType: mappedAsset.deviceModel || "",
            userRemoteOperationAllowed: mappedAsset.remoteOperationAllowed,
            controllerModel: mappedAsset.controllerModel,
            digitalOutputControls: [],
          };
        }
      }
      setComponents(componentsDeepCopy);
    }
  }

  // Handle deleting a component.
  function handleDeleteComponent(systemControlComponentId: number): void {
    const componentsDeepCopy = deepCopy(components);
    const selectedComponentIndex = componentsDeepCopy.findIndex(
      (component) => component.commandCenterComponentId === systemControlComponentId
    );
    if (selectedComponentIndex >= 0) {
      componentsDeepCopy.splice(selectedComponentIndex, 1);
      setComponents(componentsDeepCopy);
    }
  }

  // Handles moving a component to the left or right of another component when it is dropped on a drop area.
  function handleComponentDragEnd(over: Over | null, active: Active): void {
    if (over !== null) {
      const [position, targetIdText] = String(over.id).split("-");
      const targetId = parseInt(targetIdText, 10);
      const componentsDeepCopy = deepCopy(components);
      const activeIndex = componentsDeepCopy.findIndex((component) => component.commandCenterComponentId === active.id);
      const activeComponent = componentsDeepCopy.find((component) => component.commandCenterComponentId === active.id);
      if (targetId !== active.id && activeIndex > -1 && activeComponent !== undefined) {
        // Start by removing the component from the array before adding it to its new location.
        componentsDeepCopy.splice(activeIndex, 1);
        // Figure out where the component gets added back into the array.
        const targetIndex = componentsDeepCopy.findIndex(
          (component) => component.commandCenterComponentId === targetId
        );
        if (targetIndex > -1) {
          let insertIndex = targetIndex;
          if (position === "after") {
            insertIndex = targetIndex + 1;
          }
          componentsDeepCopy.splice(insertIndex, 0, activeComponent);
          setComponents(componentsDeepCopy);
        }
      }
    }
  }

  // Handle creating a component.
  function handleCreateComponent(): void {
    let newSystemControlComponentId = 1;
    components.forEach((component) => {
      if (component.commandCenterComponentId >= newSystemControlComponentId) {
        newSystemControlComponentId = component.commandCenterComponentId + 1;
      }
    });
    const newComponent = {
      commandCenterComponentId: newSystemControlComponentId,
      name: "",
      componentType: TABLE_COMPONENT_TYPE,
      attributes: [
        {
          componentAssetAttributeId: 1,
          attributeId: 0,
          attributeName: "",
          assetId: 0,
          assetName: "",
          unitShortName: null,
          unitLongName: null,
          regEnumId: null,
          currentValue: null,
          graphValues: [],
          sortPriority: 0,
          highlightingRules: [],
          modifiedSinceLoad: true,
        },
      ],
      graph: null,
      gauge: null,
      remoteOperationAsset: null,
      sortPriority: 0,
      assetName: "",
      remoteOperationEnabled: false,
    };
    setComponents((prev) => [...prev, newComponent]);
  }

  // Handle changing a component's name.
  function handleChangeComponentName(systemControlComponentId: number, name: string): void {
    const componentsDeepCopy = deepCopy(components);
    const selectedComponent = componentsDeepCopy.find(
      (component) => component.commandCenterComponentId === systemControlComponentId
    );
    if (selectedComponent !== undefined) {
      selectedComponent.name = name;
      setComponents(componentsDeepCopy);
    }
  }

  // Handle changing a component's type. Gauges only support a single attribute.
  function handleChangeComponentType(systemControlComponentId: number, componentType: string): void {
    const componentsDeepCopy = deepCopy(components);
    const selectedComponent = componentsDeepCopy.find(
      (component) => component.commandCenterComponentId === systemControlComponentId
    );
    if (selectedComponent !== undefined && selectedComponent.componentType !== componentType) {
      selectedComponent.componentType = componentType;
      if (componentType === GAUGE_COMPONENT_TYPE) {
        selectedComponent.gauge = {
          componentGaugeType: GAUGE_TYPE_TANK_ROUND,
          min: "",
          max: "",
        };
        selectedComponent.attributes = selectedComponent.attributes.slice(0, 1);
        // Gauges cannot add attributes, so make sure there is one to select.
        if (selectedComponent.attributes.length === 0) {
          selectedComponent.attributes = [createBlankAttribute(selectedComponent)];
        }
      } else {
        selectedComponent.gauge = null;
      }
      setComponents(componentsDeepCopy);
    }
  }

  // Handle changing the type of a gauge component.
  function handleChangeGaugeType(systemControlComponentId: number, gaugeType: string): void {
    const componentsDeepCopy = deepCopy(components);
    const selectedComponent = componentsDeepCopy.find(
      (component) => component.commandCenterComponentId === systemControlComponentId
    );
    if (selectedComponent !== undefined && selectedComponent.gauge !== null) {
      selectedComponent.gauge.componentGaugeType = gaugeType;
      setComponents(componentsDeepCopy);
    }
  }

  // Handle changing the minimum or maximum value of a gauge component.
  function handleChangeGaugeRange(systemControlComponentId: number, field: "min" | "max", value: string): void {
    const componentsDeepCopy = deepCopy(components);
    const selectedComponent = componentsDeepCopy.find(
      (component) => component.commandCenterComponentId === systemControlComponentId
    );
    if (selectedComponent !== undefined && selectedComponent.gauge !== null) {
      selectedComponent.gauge[field] = value;
      setComponents(componentsDeepCopy);
    }
  }

  // Keeps the live data of an attribute that is being edited in line with what is selected. Attributes that are still
  // the saved attribute get their saved data back and keep updating, while attributes that were changed stop receiving
  // updates since the data that is polled belongs to the saved attribute.
  function updateLiveDataState(systemControlComponentId: number, editedAttribute: ComponentAttribute): void {
    const savedAttribute = previousComponents
      .find((component) => component.commandCenterComponentId === systemControlComponentId)
      ?.attributes.find(
        (attribute) => attribute.componentAssetAttributeId === editedAttribute.componentAssetAttributeId
      );
    if (
      savedAttribute !== undefined &&
      savedAttribute.attributeId === editedAttribute.attributeId &&
      savedAttribute.assetId === editedAttribute.assetId
    ) {
      editedAttribute.modifiedSinceLoad = false;
      editedAttribute.currentValue = deepCopy(savedAttribute.currentValue);
      editedAttribute.graphValues = deepCopy(savedAttribute.graphValues);
    } else {
      editedAttribute.modifiedSinceLoad = true;
      editedAttribute.currentValue = null;
      editedAttribute.graphValues = [];
    }
  }

  // Handle updating a component so it has new selected attributes.
  function handleChangeComponentSelectedAttribute(
    systemControlComponentId: number,
    componentAssetAttributeId: number,
    attributeName: string
  ): void {
    const componentsDeepCopy = deepCopy(components);
    const selectedComponent = componentsDeepCopy.find(
      (component) => component.commandCenterComponentId === systemControlComponentId
    );
    if (selectedComponent !== undefined) {
      const selectedAttribute = selectedComponent.attributes.find(
        (attribute) => attribute.componentAssetAttributeId === componentAssetAttributeId
      );
      if (selectedAttribute !== undefined) {
        selectedAttribute.attributeName = attributeName;
        // Check if the current attribute name is a valid attribute.
        const mappedAttribute = attributeMap[attributeName];
        if (mappedAttribute === undefined) {
          selectedAttribute.attributeId = 0;
          selectedAttribute.unitShortName = null;
          selectedAttribute.unitLongName = null;
          selectedAttribute.regEnumId = null;
          selectedAttribute.currentValue = null;
          selectedAttribute.graphValues = [];
          selectedAttribute.highlightingRules = [];
        } else {
          selectedAttribute.attributeId = mappedAttribute.attributeId;
          selectedAttribute.unitShortName = mappedAttribute.unitShortName;
          selectedAttribute.unitLongName = mappedAttribute.unitLongName;
          selectedAttribute.regEnumId = mappedAttribute.regEnumId;
          selectedAttribute.currentValue = null;
          selectedAttribute.graphValues = [];
          selectedAttribute.highlightingRules = [];
        }
        updateLiveDataState(systemControlComponentId, selectedAttribute);
        setComponents(componentsDeepCopy);
      }
    }
  }

  // Handle setting the highlighting rules of an attribute in a component.
  function handleChangeComponentAttributeHighlightingRules(
    systemControlComponentId: number,
    componentAssetAttributeId: number,
    highlightingRules: HighlightingRule[]
  ): void {
    const componentsDeepCopy = deepCopy(components);
    const selectedComponent = componentsDeepCopy.find(
      (component) => component.commandCenterComponentId === systemControlComponentId
    );
    if (selectedComponent !== undefined) {
      const selectedAttribute = selectedComponent.attributes.find(
        (attribute) => attribute.componentAssetAttributeId === componentAssetAttributeId
      );
      if (selectedAttribute !== undefined) {
        selectedAttribute.highlightingRules = highlightingRules;
        setComponents(componentsDeepCopy);
      }
    }
  }

  // Remove an attribute from a component.
  function handleRemoveComponentSelectedAttribute(
    systemControlComponentId: number,
    componentAssetAttributeId: number
  ): void {
    const componentsDeepCopy = deepCopy(components);
    const selectedComponent = componentsDeepCopy.find(
      (component) => component.commandCenterComponentId === systemControlComponentId
    );
    if (selectedComponent !== undefined) {
      const selectedAttributeIndex = selectedComponent.attributes.findIndex(
        (attribute) => attribute.componentAssetAttributeId === componentAssetAttributeId
      );

      if (selectedAttributeIndex >= 0) {
        selectedComponent.attributes.splice(selectedAttributeIndex, 1);
        setComponents(componentsDeepCopy);
      }
    }
  }

  // Move the attribute at an index in a component to below the attribute at another index. An index of -1 moves it to
  // the top of the list.
  function handleReorderComponentSelectedAttribute(
    systemControlComponentId: number,
    fromIndex: number,
    belowIndex: number
  ): void {
    const componentsDeepCopy = deepCopy(components);
    const selectedComponent = componentsDeepCopy.find(
      (component) => component.commandCenterComponentId === systemControlComponentId
    );
    if (
      selectedComponent !== undefined &&
      fromIndex >= 0 &&
      fromIndex < selectedComponent.attributes.length &&
      belowIndex >= -1 &&
      belowIndex < selectedComponent.attributes.length
    ) {
      // Start by removing the attribute from the array before adding it to its new location. Removing an attribute that
      // was above the target shifts the target up by one.
      const [movedAttribute] = selectedComponent.attributes.splice(fromIndex, 1);
      const insertIndex = belowIndex < fromIndex ? belowIndex + 1 : belowIndex;
      if (insertIndex !== fromIndex) {
        selectedComponent.attributes.splice(insertIndex, 0, movedAttribute);
        setComponents(componentsDeepCopy);
      }
    }
  }

  // Creates an attribute without a selection for a component, which uses the component's asset.
  function createBlankAttribute(component: SystemControlComponent): ComponentAttribute {
    let newComponentAssetAttributeId = 1;
    component.attributes.forEach((attribute) => {
      if (attribute.componentAssetAttributeId >= newComponentAssetAttributeId) {
        newComponentAssetAttributeId = attribute.componentAssetAttributeId + 1;
      }
    });
    return {
      componentAssetAttributeId: newComponentAssetAttributeId,
      attributeId: 0,
      attributeName: "",
      assetId: assetMap[component.assetName]?.assetId ?? 0,
      assetName: component.assetName,
      unitShortName: null,
      unitLongName: null,
      regEnumId: null,
      currentValue: null,
      graphValues: [],
      sortPriority: 0,
      highlightingRules: [],
      modifiedSinceLoad: true,
    };
  }

  // Add an attribute to a component.
  function handleAddComponentSelectedAttribute(systemControlComponentId: number): void {
    const componentsDeepCopy = deepCopy(components);
    const selectedComponent = componentsDeepCopy.find(
      (component) => component.commandCenterComponentId === systemControlComponentId
    );
    if (selectedComponent !== undefined) {
      selectedComponent.attributes = [...selectedComponent.attributes, createBlankAttribute(selectedComponent)];
      setComponents(componentsDeepCopy);
    }
  }

  // Handle saving changes to the system control dashboard for this asset group.
  async function handleSaveChanges(): Promise<void> {
    setShowCancelChangesModal(false);
    if (systemControlIsValid()) {
      const requestBody = {
        components: components.map((component) => ({
          name: component.name,
          componentType: component.componentType,
          attributes: component.attributes.map((attribute) => ({
            attributeId: attribute.attributeId,
            assetId: attribute.assetId,
            sortPriority: attribute.sortPriority,
            highlightingRules: attribute.highlightingRules.map((rule) => ({
              comparator: rule.comparator,
              valueType: rule.valueType,
              value: rule.value,
              colorHexCode: rule.colorHexCode,
              sortPriority: rule.sortPriority,
            })),
          })),
          gauge:
            component.gauge === null
              ? null
              : {
                  componentGaugeType: component.gauge.componentGaugeType,
                  min: Number(component.gauge.min),
                  max: Number(component.gauge.max),
                },
          graph: component.graph,
          remoteOperationAssetId:
            component.remoteOperationAsset === null ? null : component.remoteOperationAsset.assetId,
          sortPriority: component.sortPriority,
        })),
      };

      setSaveLoading(true);
      const [response] = (await apiRequest(
        `${API}/company/${currentUser.companyId}/assetgroup/${assetgroupId}/command-center`,
        "PUT",
        requestBody
      )) as [Response, PutAssetGroupSystemControlResponseBody];
      setSaveLoading(false);

      if (response.ok) {
        setPreviousComponents(components);
        setInEditMode(false);
        setAssetGroupLastModifiedUtc(null);
      } else {
        setUnableToSaveErrorMessage(await getApiError(response, "Unable to update component."));
      }
    }
  }

  // Check if the current system control is valid.
  function systemControlIsValid(): boolean {
    let componentWithoutName: boolean = false;
    let componentWithoutAsset: string | null = null;
    let componentWithoutAttribute: string | null = null;
    let componentWithInvalidAttribute: string | null = null;
    let gaugeWithNonNumericAttribute: string | null = null;
    let componentWithNonNumericGauge: string | null = null;
    let componentWithInvalidGaugeRange: string | null = null;
    for (const component of components) {
      if (component.gauge !== null) {
        const nonNumericAttribute = component.attributes.find((componentAttribute) => {
          const mappedAttribute = attributes.find(
            (attribute) => attribute.attributeId === componentAttribute.attributeId
          );
          return mappedAttribute !== undefined && !isNumericAttribute(mappedAttribute);
        });
        if (nonNumericAttribute !== undefined) {
          gaugeWithNonNumericAttribute = component.name;
          break;
        } else if (!isNumeric(component.gauge.min) || !isNumeric(component.gauge.max)) {
          componentWithNonNumericGauge = component.name;
          break;
        } else if (Number(component.gauge.min) >= Number(component.gauge.max)) {
          componentWithInvalidGaugeRange = component.name;
          break;
        }
      }
      if (component.name.length === 0) {
        componentWithoutName = true;
      } else if (component.attributes.length === 0) {
        componentWithoutAttribute = component.name;
        break;
      } else {
        for (const attribute of component.attributes) {
          if (attribute.assetId === 0) {
            componentWithoutAsset = component.name;
            break;
          }
          if (attribute.attributeId === 0) {
            componentWithInvalidAttribute = component.name;
            break;
          }
        }
      }
    }
    if (gaugeWithNonNumericAttribute !== null) {
      setUnableToSaveErrorMessage(`Gauge '${gaugeWithNonNumericAttribute}' must use a numeric attribute.`);
      return false;
    } else if (componentWithNonNumericGauge !== null) {
      setUnableToSaveErrorMessage(`Gauge '${componentWithNonNumericGauge}' must have a numeric minimum and maximum.`);
      return false;
    } else if (componentWithInvalidGaugeRange !== null) {
      setUnableToSaveErrorMessage(
        `Gauge '${componentWithInvalidGaugeRange}' must have a minimum that is less than its maximum.`
      );
      return false;
    } else if (componentWithoutName) {
      setUnableToSaveErrorMessage(`All components are required to have a title.`);
      return false;
    } else if (componentWithoutAsset !== null) {
      setUnableToSaveErrorMessage(`Component '${componentWithoutAsset}' does not have a valid asset selected.`);
      return false;
    } else if (componentWithoutAttribute !== null) {
      setUnableToSaveErrorMessage(`Component '${componentWithoutAttribute}' does not have any attributes selected.`);
      return false;
    } else if (componentWithInvalidAttribute !== null) {
      setUnableToSaveErrorMessage(`Component '${componentWithInvalidAttribute}' has an invalid attribute selected.`);
      return false;
    } else {
      return true;
    }
  }

  // Returns whether an attribute holds a number, which excludes boolean and enumerated attributes.
  function isNumericAttribute(attribute: Attribute): boolean {
    return !attribute.isBoolean && attribute.regEnumId === null;
  }

  // Returns whether a gauge limit is a finite number, which can be an integer or a decimal.
  function isNumeric(value: number | string): boolean {
    return String(value).trim().length > 0 && isFinite(Number(value));
  }

  // Handle canceling changes to the system control dashboard for this asset group, but first confirming.
  function handleCancelChanges(): void {
    if (componentsHaveChanged()) {
      setShowCancelChangesModal(true);
    } else {
      handleDiscardChanges();
    }
  }

  // Handle discarding changes to the system control without verification of change.
  function handleDiscardChanges(): void {
    setShowCancelChangesModal(false);
    setComponents(previousComponents);
    setInEditMode(false);
  }

  // Returns whether changes have been made to components for the current asset group.
  function componentsHaveChanged(): boolean {
    return JSON.stringify(previousComponents) !== JSON.stringify(components);
  }

  // Generates a map for an array of assets keyed on asset names.
  function getAssetMap(assets: Asset[]): AssetMap {
    const assetMap: AssetMap = {};
    assets.forEach((asset) => (assetMap[asset.assetName] = asset));
    return assetMap;
  }

  // Generates a map for an array of attributes keyed on attribute names.
  function getAttributeMap(attributes: Attribute[]): AttributeMap {
    const attributeMap: AttributeMap = {};
    attributes.forEach((attribute) => (attributeMap[attribute.name] = attribute));
    return attributeMap;
  }

  return (
    <Fragment>
      <Spinner loading={mapLoading || componentLoading || saveLoading} />
      <Toast
        title="Unable to Save Changes"
        message={unableToSaveErrorMessage}
        show={unableToSaveErrorMessage.length > 0}
        onClose={() => setUnableToSaveErrorMessage("")}
        type="error"
      />
      <GroupAndEditSelector
        loading={mapLoading || componentLoading || saveLoading}
        assetgroups={assetgroups}
        assetgroupId={assetgroupId}
        inEditMode={inEditMode}
        disabled={failedToLoad}
        changesHaveBeenMade={false}
        onSelectAssetgroup={(assetgroupId) => setAssetgroupId(assetgroupId)}
        onEditSystemControl={() => setInEditMode(true)}
        onSaveEditSystemControl={() => handleSaveChanges()}
        onCancelEditSystemControl={() => handleCancelChanges()}
      />
      <SaveChangesModal
        showModal={showCancelChangesModal}
        title="Changes have not been saved!"
        content="Are you sure that you want to discard your changes?"
        onClose={() => setShowCancelChangesModal(false)}
        onSave={() => handleSaveChanges()}
        onNoSave={() => handleDiscardChanges()}
      />
      <div className="px-4 pb-3">
        <DndContext onDragEnd={(e) => handleComponentDragEnd(e.over, e.active)}>
          <div className={`row ${inEditMode ? "gy-2 gx-0" : "g-2"}`}>
            {components.map((component, i) => (
              <SystemControlComponentWrapper
                key={component.commandCenterComponentId}
                systemControlComponentId={component.commandCenterComponentId}
                isLastComponent={i === components.length - 1}
                name={component.name}
                componentType={component.componentType}
                gauge={component.gauge}
                remoteOperationEnabled={component.remoteOperationEnabled}
                assets={assets}
                attributes={attributes}
                enumerationMap={enumerationMap}
                selectedAttributes={component.attributes}
                assetName={component.assetName}
                inEditMode={inEditMode}
                onDelete={() => handleDeleteComponent(component.commandCenterComponentId)}
                onChangeComponentName={(componentName) =>
                  handleChangeComponentName(component.commandCenterComponentId, componentName)
                }
                onChangeComponentType={(componentType) =>
                  handleChangeComponentType(component.commandCenterComponentId, componentType)
                }
                onChangeGaugeType={(gaugeType) => handleChangeGaugeType(component.commandCenterComponentId, gaugeType)}
                onChangeGaugeMin={(min) => handleChangeGaugeRange(component.commandCenterComponentId, "min", min)}
                onChangeGaugeMax={(max) => handleChangeGaugeRange(component.commandCenterComponentId, "max", max)}
                onChangeAssetName={(assetName) => handleChangeAssetName(component.commandCenterComponentId, assetName)}
                onToggleRemoteOperationEnabled={() =>
                  handleToggleRemoteOperationEnabled(component.commandCenterComponentId)
                }
                onChangeAttribute={(componentAssetAttributeId, attributeName) =>
                  handleChangeComponentSelectedAttribute(
                    component.commandCenterComponentId,
                    componentAssetAttributeId,
                    attributeName
                  )
                }
                onChangeHighlightingRules={(componentAssetAttributeId, highlightingRules) =>
                  handleChangeComponentAttributeHighlightingRules(
                    component.commandCenterComponentId,
                    componentAssetAttributeId,
                    highlightingRules
                  )
                }
                onRemoveAttribute={(componentAssetAttributeId) =>
                  handleRemoveComponentSelectedAttribute(component.commandCenterComponentId, componentAssetAttributeId)
                }
                onAddAttribute={() => handleAddComponentSelectedAttribute(component.commandCenterComponentId)}
                onReorderAttribute={(fromIndex, belowIndex) =>
                  handleReorderComponentSelectedAttribute(component.commandCenterComponentId, fromIndex, belowIndex)
                }
              />
            ))}
            {inEditMode && (
              <AddResourceButton
                text="Add Component"
                disabled={components.length >= MAX_COMPONENTS_IN_SYSTEM_CONTROL}
                onAddResource={() => handleCreateComponent()}
              />
            )}
          </div>
        </DndContext>
        {failedToLoad && (
          <Card title="Failed to Load">
            <div className={`${styles.emptyList} py-5`}>
              This page could not load due to an internal server error. Please try again later.
            </div>
          </Card>
        )}
        {!failedToLoad && !mapLoading && !componentLoading && !saveLoading && assetgroupId === 0 && (
          <Card title="Select an Asset Group">
            <div className={`${styles.emptyList} py-5`}>
              Please select a specific asset group to view that asset group&apos;s system control.
            </div>
          </Card>
        )}
        {!failedToLoad &&
          !mapLoading &&
          !componentLoading &&
          !saveLoading &&
          assetgroupId !== 0 &&
          !inEditMode &&
          components.length === 0 && (
            <Card title="System Control Not Setup">
              <div className={`${styles.emptyList} py-5`}>
                The system control dashboard has not been setup for this asset group yet.{" "}
                {userHasPermission([[UPDATE_ASSET_GROUPS_PERMISSION]])
                  ? "Press the 'Edit' button to begin setting up components for the dashboard."
                  : ""}
              </div>
            </Card>
          )}
      </div>
      <datalist id="datalist-assets">
        {assets.map((asset) => (
          <option key={asset.assetId} value={asset.assetName} />
        ))}
      </datalist>
      <datalist id="datalist-attributes-numeric">
        {attributes.filter(isNumericAttribute).map((attribute) => (
          <option key={attribute.attributeId} value={attribute.name} />
        ))}
      </datalist>
      <datalist id="datalist-attributes">
        {attributes.map((attribute) => (
          <option key={attribute.attributeId} value={attribute.name} />
        ))}
      </datalist>
    </Fragment>
  );
}

interface SystemControlComponent {
  commandCenterComponentId: number;
  name: string;
  componentType: string;
  attributes: ComponentAttribute[];
  gauge: ComponentGauge | null;
  graph: ComponentGraph | null;
  remoteOperationAsset: RemoteOperationAsset | null;
  sortPriority: number;
  assetName: string;
  remoteOperationEnabled: boolean;
}

interface ComponentAttribute {
  componentAssetAttributeId: number;
  attributeId: number;
  attributeName: string;
  assetId: number;
  assetName: string;
  unitShortName: string | null;
  unitLongName: string | null;
  regEnumId: number | null;
  currentValue: AttributeValue | null;
  graphValues: AttributeValue[];
  sortPriority: number;
  highlightingRules: HighlightingRule[];
  modifiedSinceLoad: boolean;
}

interface Attribute {
  attributeId: number;
  regEnumId: number | null;
  name: string;
  code: string;
  unitShortName: string;
  unitLongName: string;
  isBoolean: boolean;
  isHighlightable: boolean;
}

interface EnumerationMap {
  [key: string]: EnumerationIndexMap;
}

interface EnumerationIndexMap {
  [key: string]: string;
}

interface AttributeValue {
  value: string | null;
  timestamp: string | null;
}

interface HighlightingRule {
  comparator: string;
  value: string;
  valueType: string;
  colorHexCode: string;
  sortPriority: number;
}

interface ComponentGauge {
  componentGaugeType: string;
  min: number | string;
  max: number | string;
}

interface ComponentGraph {
  historyGraphType: string;
  historyGraphAxisType: string;
  numberOfDaysShown: number;
}

interface RemoteOperationAsset {
  assetId: number;
  assetName: string;
  deviceType: string;
  userRemoteOperationAllowed: boolean;
  controllerModel: string | null;
  digitalOutputControls: DigitalOutputControl[];
}

interface DigitalOutputControl {
  position: number;
  operationLabel: string;
  labelWhenTrue: string;
  labelWhenFalse: string;
}

interface DashboardAssetgroup {
  assetgroupId: number;
  name: string;
}

interface Asset {
  assetId: number;
  assetName: string;
  assetNickname: string;
  controllerModel: string | null;
  controllerName: string | null;
  deviceModel: string | null;
  deviceIdentifier: string | null;
  digitalOutputControls: DigitalOutputControl[];
  remoteOperationIsLocked: boolean;
  remoteOperationAllowed: boolean;
}

interface DigitalOutputControl {
  position: number;
  operationLabel: string;
  labelWhenTrue: string;
  labelWhenFalse: string;
}

interface AttributeMap {
  [key: string]: Attribute;
}

interface AssetMap {
  [key: string]: Asset;
}
